/**
 * Stylesheet Injection Utilities
 *
 * Reusable functions for injecting and managing dynamic stylesheets and external resources.
 * Can be used for fonts, shadows, border-radius, and any dynamic CSS.
 */

/**
 * Injects or updates a stylesheet in the document head
 *
 * @param styleId - Unique ID for the style element
 * @param css - CSS content to inject
 */
export function injectStylesheet(styleId: string, css: string): void {
  let styleElement = document.getElementById(styleId) as HTMLStyleElement;

  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = styleId;
    document.head.appendChild(styleElement);
  }

  styleElement.textContent = css;
}

/**
 * Removes a stylesheet from the document head
 *
 * @param styleId - ID of the style element to remove
 */
export function removeStylesheet(styleId: string): void {
  const styleElement = document.getElementById(styleId);
  if (styleElement) {
    styleElement.remove();
  }
}

// TODO: use with external fonts support (to pass google font links and load)
/**
 * Injects external stylesheet links (e.g., Google Fonts)
 *
 * @param linkIdPrefix - Prefix for link element IDs
 * @param urls - Array of URLs to inject
 */
export function injectExternalLinks(linkIdPrefix: string, urls: string[]): void {
  // Remove existing links with this prefix
  removeExternalLinks(linkIdPrefix);

  // Inject new links
  urls.forEach((url, index) => {
    const link = document.createElement('link');
    link.id = `${linkIdPrefix}-${index}`;
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  });
}

/**
 * Removes external stylesheet links by prefix
 *
 * @param linkIdPrefix - Prefix of link element IDs to remove
 */
export function removeExternalLinks(linkIdPrefix: string): void {
  const existingLinks = document.querySelectorAll(`link[id^="${linkIdPrefix}"]`);
  existingLinks.forEach((link) => link.remove());
}

/**
 * Generates CSS classes from a configuration object
 *
 * @param classMapping - Mapping of keys to CSS class names
 * @param configs - Configuration object with CSS properties
 * @param cssGenerator - Function that generates CSS properties for each config
 * @returns CSS string
 *
 * @example
 * const css = generateCSSClasses(
 *   { sm: 'shadow-sm', md: 'shadow-md' },
 *   { sm: '0 1px 2px rgba(0,0,0,0.1)', md: '0 4px 6px rgba(0,0,0,0.1)' },
 *   (value) => `box-shadow: ${value};`
 * );
 */
export function generateCSSClasses<T extends Record<string, any>>(
  classMapping: Record<string, string>,
  configs: T,
  cssGenerator: (config: any, key: string) => string
): string {
  const styles: string[] = [];

  Object.entries(configs).forEach(([key, config]) => {
    if (!config) return;

    const className = classMapping[key];
    if (!className) return;

    const cssProperties = cssGenerator(config, key);

    styles.push(
      `
.${className} {
${cssProperties}
}
    `.trim()
    );
  });

  return styles.join('\n\n');
}

/**
 * Complete cleanup of all injected resources
 *
 * @param styleId - ID of stylesheet to remove
 * @param linkIdPrefix - Prefix of external links to remove (optional)
 */
export function cleanupInjectedResources(styleId: string, linkIdPrefix?: string): void {
  removeStylesheet(styleId);

  if (linkIdPrefix) {
    removeExternalLinks(linkIdPrefix);
  }
}
